import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Brain, Clock, TrendingDown, AlertTriangle } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const SuezCanalArticle: React.FC = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-6">
          <Button 
            variant="ghost" 
            onClick={() => window.history.back()}
            className="hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.backToHighlights')}
          </Button>
          <LanguageSwitcher />
        </div>

        <article className="max-w-4xl mx-auto">
          <header className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Badge className="bg-blue-500 text-white">
                <Brain className="h-3 w-3 mr-1" />
                {t('article.suezcanal.badge')}
              </Badge>
              <span className="text-gray-500">{t('article.suezcanal.date')}</span>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('article.suezcanal.title')}
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              {t('article.suezcanal.summary')}
            </p>
          </header>

          <div className="mb-8">
            <img 
              src="https://www.dhl.com/content/dam/dhl/global/delivered/images/social-share-1200x630/d157-dl-business-logistics.jpg"
              alt="Amazon Belgium market leadership and e-commerce growth" 
              className="w-full h-64 object-cover rounded-lg shadow-lg"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Clock className="h-5 w-5 mr-2 text-green-600" />
                  {t('suezcanal.stats.transit.label')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-green-600">{t('suezcanal.stats.transit.value')}</div>
                <p className="text-sm text-gray-600">{t('suezcanal.stats.transit.desc')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <TrendingDown className="h-5 w-5 mr-2 text-blue-600" />
                  {t('suezcanal.stats.freight.label')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-blue-600">{t('suezcanal.stats.freight.value')}</div>
                <p className="text-sm text-gray-600">{t('suezcanal.stats.freight.desc')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <AlertTriangle className="h-5 w-5 mr-2 text-orange-600" />
                  {t('suezcanal.stats.port.label')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-orange-600">{t('suezcanal.stats.port.value')}</div>
                <p className="text-sm text-gray-600">{t('suezcanal.stats.port.desc')}</p>
              </CardContent>
            </Card>
          </div>

          <div className="prose max-w-none">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('suezcanal.section1.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('suezcanal.section1.content')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('suezcanal.section2.title')}</h3>
            <div className="bg-orange-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">{t('suezcanal.section2.subtitle')}</h4>
              <div className="whitespace-pre-line text-sm text-gray-700">
                {t('suezcanal.section2.content')}
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('suezcanal.section3.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('suezcanal.section3.content')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('suezcanal.section4.title')}</h3>
            <div className="bg-blue-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">{t('suezcanal.section4.subtitle')}</h4>
              <div className="whitespace-pre-line text-sm text-gray-700">
                {t('suezcanal.section4.content')}
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('suezcanal.section5.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('suezcanal.section5.content')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('suezcanal.section6.title')}</h3>
            <div className="bg-yellow-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">{t('suezcanal.section6.subtitle')}</h4>
              <div className="whitespace-pre-line text-sm text-gray-700">
                {t('suezcanal.section6.content')}
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('suezcanal.section7.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('suezcanal.section7.content')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('suezcanal.section8.title')}</h3>
            <div className="bg-green-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">{t('suezcanal.section8.subtitle')}</h4>
              <div className="whitespace-pre-line text-sm text-gray-700">
                {t('suezcanal.section8.content')}
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('suezcanal.section9.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('suezcanal.section9.content')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('suezcanal.section10.title')}</h3>
            <div className="bg-purple-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">{t('suezcanal.section10.subtitle')}</h4>
              <div className="whitespace-pre-line text-sm text-gray-700">
                {t('suezcanal.section10.content')}
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('suezcanal.strategic.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('suezcanal.strategic.content')}
            </p>

            <div className="bg-gray-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">Source Information:</h4>
              <p className="text-sm text-gray-600 mb-2">
                {t('suezcanal.source')}
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                className="text-blue-600 border-blue-200"
                onClick={() => window.open('https://logisticsviewpoints.com/2025/12/22/ai-in-logistics-what-actually-worked-in-2025-and-what-will-scale-in-2026/', '_blank')}
              >
                View Original Analysis
              </Button>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default SuezCanalArticle;